import { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useCreateAccountMutation, useGetAccountQuery } from 'store/api';
import { auth } from 'utils/firebase';
import { createLogoutRecord } from 'utils/api';
import { signOut, reload, signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from 'firebase/auth';

import Button from 'components/Button';
import Input from 'components/Input';
import ErrorMessage from 'components/ErrorMessage';
import Modal from 'components/Modal';
import TermsModal from 'components/modals/TermsModal';
import MPModal from 'components/modals/RedirectMPModal';
import spinner from 'assets/images/icon-loading-claim.png';

export default function Setup() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [createUser, { isLoading: isLoadingAccount }] = useCreateAccountMutation();
  const {
    data: account,
    isLoading: isLoadingGetAccount,
    isError: isErrorGetAccount,
    error: accountError,
    refetch: refetchAccount,
  } = useGetAccountQuery({}, { refetchOnMountOrArgChange: true });

  const [usernameError, setUsernameError] = useState(null);
  const [mPSub, setMPSub] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [redirect, setredirect] = useState(false);
  const [step, setStep] = useState(0);
  const [openTerms, setOpenTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [reAuthAttempted, setReAuthAttempted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm();

  const validate = {
    username: watch('username'),
    name: watch('name'),
    plan: watch('plan'),
    terms: watch('terms'),
  };

  useEffect(() => {
    if (accountError?.status === 403) {
      navigate('/sign-in', { replace: true });
    }
  }, [accountError, navigate]);

  // Check if user returned from email verification (Safari compatibility)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('emailVerified') === 'true') {
      console.log('User returned from email verification - refreshing account data');
      // Remove the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      // Force account refresh
      refetchAccount();
    }
  }, [refetchAccount]);

  // Secure re-authentication helper
  const cleanupTempCredentials = () => {
    window.localStorage.removeItem('tempSignInCredentials');
  };

  const attemptSecureReAuth = useCallback(async () => {
    if (reAuthAttempted) return false; // Prevent loops

    try {
      const tempCredsString = window.localStorage.getItem('tempSignInCredentials');
      if (!tempCredsString) return false;

      const tempCreds = JSON.parse(tempCredsString);
      const now = Date.now();
      const TEN_MINUTES = 10 * 60 * 1000; // 10 minutes in ms

      // Check if credentials expired (10 min max)
      if (now - tempCreds.ts > TEN_MINUTES) {
        console.log('Temp credentials expired, cleaning up');
        cleanupTempCredentials();
        return false;
      }

      // Set persistence before re-login
      await setPersistence(auth, browserLocalPersistence);

      // Attempt re-authentication
      await signInWithEmailAndPassword(auth, tempCreds.email, tempCreds.password);
      console.log('Re-authenticated user successfully');

      setReAuthAttempted(true);
      return true;

    } catch (error) {
      console.error('Re-authentication failed:', error);
      return false;
    } finally {
      // Always cleanup credentials after attempt (success or error)
      cleanupTempCredentials();
    }
  }, [reAuthAttempted]);

  // Check email verification status periodically when on verification screen
  useEffect(() => {
    if (!account?.email_verified) {
      const checkVerification = async () => {
        setCheckingVerification(true);
        try {
          // If no current user (session lost), try secure re-authentication
          if (!auth.currentUser) {
            // Wait a bit for auth state to settle (especially important on mobile)
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Check again after waiting
            if (!auth.currentUser) {
              const reAuthSuccess = await attemptSecureReAuth();
              if (!reAuthSuccess) {
                // Re-auth failed or no valid credentials, redirect to sign-in
                navigate('/sign-in', { replace: true });
                return;
              }
            }
          }

          // Reload the user to get updated email verification status
          await reload(auth.currentUser);

          // If email is verified, refetch account data and stop checking
          if (auth.currentUser.emailVerified) {
            await refetchAccount();
            setCheckingVerification(false);
            return; // Stop the interval
          }
        } catch (error) {
          console.error('Error checking email verification:', error);
          // On any error, cleanup and potentially redirect
          cleanupTempCredentials();
        } finally {
          setCheckingVerification(false);
        }
      };

      // Wait before starting verification checks to allow auth state to settle
      const initialDelay = setTimeout(() => {
        checkVerification();

        // Then check every 3 seconds, but stop if verified
        const interval = setInterval(() => {
          if (account?.email_verified) {
            clearInterval(interval);
            return;
          }
          checkVerification();
        }, 3000);

        // Cleanup interval when component unmounts or effect re-runs
        return () => clearInterval(interval);
      }, 2000); // Wait 2 seconds before starting

      // Cleanup timeout when component unmounts or effect re-runs
      return () => {
        clearTimeout(initialDelay);
      };
    }
  }, [account?.email_verified, refetchAccount, reAuthAttempted, navigate, attemptSecureReAuth]);

  const handleTermsResult = (accepted) => {
    setAcceptedTerms(accepted);
    setValue('terms', accepted);

    setOpenTerms(false);
  };
  const handleFirstStep = async (data) => {
    setUsernameError(null);
    clearErrors('username');

    if (!account?.username) {
      try {
        const response = await createUser({
          type: 'artist',
          data: {
            username: data.username,
            full_name: data.name,
            email: account?.email,
            login_method: account?.login_method,
          },
        });

        if ('error' in response) {
          if (
            response.error.data?.error?.some(
              (err) => typeof err === 'string' && err.toLowerCase().includes('username already used')
            )
          ) {
            const errorMessage =
              i18n.language === 'es'
                ? 'El nombre de usuario ya existe. Elige uno diferente.'
                : 'Username already exists. Select a different one.';
            setUsernameError(errorMessage);
            setError(
              'username',
              {
                type: 'manual',
                message: errorMessage,
              },
              { shouldFocus: true }
            );
            return;
          }
        }

        await refetchAccount();
      } catch (error) {
        console.error('Error inesperado:', error);
      }
    }
  };

  const handleCloseRedirect = () => {
    setredirect(false);
  };

  const handleSignOut = async () => {
    signOut(auth);
    createLogoutRecord();
  };

  const stepOne = () => (
    <>
      <div className='flex flex-col items-center px-6'>
        <h2>{t('setup.title')}</h2>
      </div>
      <div className='w-full max-w-[480px]'>
        <form onSubmit={handleSubmit(handleFirstStep)}>
          <div className='flex flex-col gap-6 mb-6'>
            <Input value={account?.email} type='email' name='email' label='Email' disabled />
            <Input
              type='text'
              name='username'
              label='Usuario'
              required
              autoComplete='off'
              noWhiteSpace
              register={register}
              error={errors.username?.message || usernameError}
            />
            <Input
              type='text'
              name='name'
              label='Nombre de artista'
              required
              autoComplete='off'
              register={register}
              error={errors.name && t('setup.required_field')}
            />
          </div>
          {errors.terms && (
            <div className='flex items-center justify-end h-14'>
              <ErrorMessage show={errors.terms} message={t('setup.accept_terms')} />
            </div>
          )}
          <div className='flex items-center gap-3 mb-8'>
            <input
              type='checkbox'
              name='terms'
              id='terms-and-conditions'
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                setValue('terms', e.target.checked);
              }}
            />
            <label htmlFor='terms-and-conditions'>
              {t('setup.step_two.terms')}{' '}
              <span className='text-brand-gold hover:cursor-pointer' onClick={() => setOpenTerms(true)}>
                {t('setup.step_two.terms_link')}
              </span>
            </label>
          </div>
          <Button
            style='primary'
            type='submit'
            text={t('setup.continue')}
            disabled={!validate.username || !validate.name || !validate.terms || isLoadingAccount}
            loading={isLoadingAccount}
          />
        </form>
        <div className='flex items-center justify-center mt-10'>
          <button className='w-auto bg-neutral-silver-600 py-3 px-6 rounded-[10px]' onClick={handleSignOut}>
            {t('profile.logout')}
          </button>
        </div>
        <Modal show={openTerms} setShow={setOpenTerms}>
          <TermsModal onResult={handleTermsResult} />
        </Modal>
      </div>
    </>
  );

  const LoadingComponent = () => (
    <div className='flex justify-center items-center h-full'>
      <img src={spinner} alt='Loading' width={40} height={40} className='animate-spin' />
      <span className='ml-2'>Cargando...</span>
    </div>
  );

  if (isLoadingGetAccount) {
    return <LoadingComponent />;
  }

  if (isErrorGetAccount) {
    return (
      <div className='flex flex-col items-center justify-center h-full'>
        <h2 className='text-red-500 mb-4'>Error al cargar los datos de la cuenta</h2>
        <Button style='primary' text='Reintentar' onClick={() => refetchAccount()} />
      </div>
    );
  }

  if (!account) {
    return (
      <div className='flex flex-col items-center justify-center h-full'>
        <h2 className='mb-4'>No se encontraron datos de cuenta</h2>
        <Button style='primary' text='Volver al inicio' onClick={() => (window.location.href = '/')} />
      </div>
    );
  }

  if (account?.subscription?.plan && !mPSub) {
    return <Navigate to='/my-chest' replace />;
  }

  if (!account?.email_verified) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        className='flex flex-col gap-4 px-4 items-center'
      >
        <h1 className='text-[76px] text-center' style={{ lineHeight: '68px', marginTop: '10%' }}>
          {t('global.verify_email.title')}
        </h1>
        <div className='flex flex-col items-center text-lg mb-6'>
          <span className='text-neutral-silver-200'>{t('global.verify_email.email_sent_to')}</span>
          <span>{account?.email}</span>
        </div>
        {checkingVerification && (
          <div className='flex items-center justify-center mb-4'>
            <img src={spinner} alt='Loading' width={20} height={20} className='animate-spin mr-2' />
            <span className='text-neutral-silver-300 text-sm'>Verificando...</span>
          </div>
        )}
        <div className='text-neutral-silver-300 text-sm mb-3 text-center'>{t('global.password_reset.tip')}</div>
        <div className='flex items-center justify-center'>
          <a
            href='https://api.whatsapp.com/send?phone=5491171804726&text=Hola%2C%20quiero%20grabar%20en%20el%20Chestudio!%F0%9F%91%8B'
            className='text-brand-gold h-10 md:h-auto hover:text-brand-bronze font-semibold text-lg py-1.5'
            target='_blank'
            rel='noreferrer'
          >
            {t('global.verify_email.issues')}
          </a>
        </div>
        <button className='w-auto py-3 px-6 rounded-[10px] text-brand-gold' onClick={handleSignOut}>
          {t('setup.another_email')}
        </button>
      </motion.div>
    );
  }

  return (
    <div className='flex flex-col gap-8 items-center justify-center h-full pt-10 pb-10 md:px-[120px] md:py-20 w-full'>
      {step === 0 ? stepOne() : <LoadingComponent />}
      <Modal show={redirect}>
        <MPModal handleClose={handleCloseRedirect} account={account} selectedPlan={selectedPlan} setMPSub={setMPSub} />
      </Modal>
    </div>
  );
}
